import { create } from "zustand";
import { persist } from "zustand/middleware";
import { generateUUID } from "../utils/uuid";

// Session management store
export const useSessionStore = create(
  persist(
    (set, get) => ({
      currentSessionId: null,
      preAuthSessionId: null, // Store session ID from before authentication
      savedSessions: [], // Array of { id, name, timestamp, preview }
      userAuthState: 'guest', // 'guest', 'signing_in', 'authenticated'
      isMigrating: false, // True while guest rooms are being migrated after sign-in/sign-up
      pendingAuthRedirect: null, // URL to navigate to after auth + migration complete
      userRoomIds: [], // Track room IDs created/saved by this user

      // Generate or get current session ID
      getOrCreateSessionId: () => {
        // First check for URL session (takes priority)
        const urlParams = new URLSearchParams(window.location.search);
        const urlSessionId = urlParams.get("session");
        if (urlSessionId) {
          set({ currentSessionId: urlSessionId });
          return urlSessionId;
        }

        // Then check current session
        const current = get().currentSessionId;
        if (current) return current;

        // Generate new session
        const newSessionId = generateUUID();
        set({ currentSessionId: newSessionId });
        return newSessionId;
      },

      // Set current session ID (when loading)
      setCurrentSessionId: (sessionId) => set({ currentSessionId: sessionId }),

      // Create new session
      createNewSession: () => {
        const newSessionId = generateUUID();
        set({ currentSessionId: newSessionId });
        return newSessionId;
      },

      // Add session to saved list
      addSavedSession: (sessionData) => {
        set((state) => ({
          savedSessions: [
            ...state.savedSessions.filter((s) => s.id !== sessionData.id), // Remove duplicates
            sessionData,
          ].slice(-20), // Keep only last 20 sessions
        }));
      },

      // Remove session from saved list
      removeSavedSession: (sessionId) => {
        set((state) => ({
          savedSessions: state.savedSessions.filter((s) => s.id !== sessionId),
        }));
      },

      // Clear current session
      clearCurrentSession: () => set({ currentSessionId: null }),

      // Handle authentication state changes
      setUserAuthState: (state) => set({ userAuthState: state }),

      // Track whether guest-room migration is in progress
      setIsMigrating: (value) => set({ isMigrating: value }),

      // Store the URL to navigate to after auth + migration complete
      // Used because OAuth flows (Google, etc.) drop Clerk's redirectUrl during the redirect cycle
      setPendingAuthRedirect: (url) => set({ pendingAuthRedirect: url }),
      clearPendingAuthRedirect: () => set({ pendingAuthRedirect: null }),

      // Preserve session ID when user starts authentication
      preserveSessionForAuth: () => {
        const current = get().currentSessionId;
        if (current) {
          set({ preAuthSessionId: current, userAuthState: 'signing_in', isMigrating: true });
        }
      },

      // Restore session continuity after authentication
      restoreSessionContinuity: () => {
        const preAuthSession = get().preAuthSessionId;
        if (preAuthSession) {
          // Keep using the same session ID for continuity
          set({
            currentSessionId: preAuthSession,
            userAuthState: 'authenticated',
            preAuthSessionId: null // Clear the temporary storage
          });
          return preAuthSession;
        }
        set({ userAuthState: 'authenticated' });
        return get().currentSessionId;
      },

      // Track room ID when user creates/saves a room
      addUserRoom: (roomId) => {
        set((state) => {
          const roomIds = state.userRoomIds || [];
          if (!roomIds.includes(roomId)) {
            return { userRoomIds: [...roomIds, roomId] };
          }
          return state;
        });
      },

      // Get all tracked room IDs
      getUserRoomIds: () => {
        return get().userRoomIds || [];
      },

      // Clear room IDs (when signing out)
      clearUserRoomIds: () => set({ userRoomIds: [] }),
    }),
    {
      name: "session-store",
      // Exclude isMigrating from persistence — a stale true in localStorage blocks
      // EditorPage.loadRoom on startup before App.js can reset it (child effects run
      // before parent effects in React). Always start fresh each session.
      partialize: (state) => {
        const { isMigrating, ...rest } = state;
        return rest;
      },
    }
  )
);
